import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const TXN_TYPES = [
  { v: "commercial_purchase", l: "Commercial Purchase" },
  { v: "seller_financed_purchase", l: "Seller-Financed Purchase" },
  { v: "business_and_real_estate", l: "Business + Real Estate" },
  { v: "land_purchase", l: "Land Purchase" },
  { v: "owner_user_acquisition", l: "Owner-User Acquisition" },
  { v: "investment_acquisition", l: "Investment Acquisition" },
];

export default function NewTransaction() {
  const [form, setForm] = useState({
    property_address: "",
    listing_url: "",
    client_name: "",
    buyer_entity: "",
    client_email: "",
    client_phone: "",
    buyers_broker: "",
    brokerage: "",
    transaction_type: "commercial_purchase",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/transactions", form);
      toast.success("Transaction created");
      navigate(`/transactions/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="new-transaction-page" className="max-w-3xl">
      <Link to="/transactions" className="text-xs uppercase tracking-widest text-slate-500 hover:text-brass inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back
      </Link>
      <div className="kicker mb-2">Step 1</div>
      <h1 className="text-4xl font-display mb-1">Create transaction</h1>
      <p className="text-sm text-slate-500 mb-8 max-w-xl">
        Start with the property and buyer. You&apos;ll upload listing documents on the next screen.
      </p>

      <form onSubmit={submit} className="grid-panel p-6 space-y-6" data-testid="new-txn-form">
        <FormSection title="Property">
          <Field label="Property Address" required
            value={form.property_address} onChange={(v) => upd("property_address", v)}
            placeholder="1234 Main St, Portland, OR 97201" testid="field-address" />
          <Field label="Listing URL" value={form.listing_url}
            onChange={(v) => upd("listing_url", v)} placeholder="https://loopnet.com/…" testid="field-listing-url" />
        </FormSection>

        <FormSection title="Buyer / Client">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Client Name" required value={form.client_name}
              onChange={(v) => upd("client_name", v)} placeholder="John Smith" testid="field-client-name" />
            <Field label="Buyer Entity" value={form.buyer_entity}
              onChange={(v) => upd("buyer_entity", v)} placeholder="Smith Holdings LLC" testid="field-buyer-entity" />
            <Field label="Client Email" type="email" value={form.client_email}
              onChange={(v) => upd("client_email", v)} placeholder="client@example.com" testid="field-client-email" />
            <Field label="Client Phone" value={form.client_phone}
              onChange={(v) => upd("client_phone", v)} placeholder="(555) 000-0000" testid="field-client-phone" />
          </div>
        </FormSection>

        <FormSection title="Representation">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Buyer's Broker" value={form.buyers_broker}
              onChange={(v) => upd("buyers_broker", v)} placeholder="Jane Broker" testid="field-buyers-broker" />
            <Field label="Brokerage" value={form.brokerage}
              onChange={(v) => upd("brokerage", v)} placeholder="Cushman & Wakefield" testid="field-brokerage" />
          </div>
        </FormSection>

        <FormSection title="Transaction Type">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {TXN_TYPES.map((t) => (
              <button
                type="button" key={t.v}
                onClick={() => upd("transaction_type", t.v)}
                data-testid={`txn-type-${t.v}`}
                className={`text-left px-3 py-3 rounded-sm border text-sm transition-colors duration-150 ${
                  form.transaction_type === t.v
                    ? "border-brass bg-brass-50 text-navy-900"
                    : "border-border bg-white hover:border-brass/50"
                }`}
              >
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Type</div>
                <div className="font-medium">{t.l}</div>
              </button>
            ))}
          </div>
        </FormSection>

        <div className="pt-2 flex justify-end gap-3 border-t border-border">
          <Link to="/transactions" className="btn-ghost">Cancel</Link>
          <button type="submit" disabled={loading} className="btn-brass" data-testid="create-txn-submit">
            {loading ? "Creating…" : "Create & Continue"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div>
      <div className="kicker mb-3">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">{label}</label>
      <input
        type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testid}
        className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass"
      />
    </div>
  );
}
